import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import FormElement, { type FormElementProps } from "@/components/form/form-element";
import { emit } from "@/internals/events";
import { WithTooltipMixin, type WithTooltipProps } from "@/internals/mixins/with-tooltip-mixin";

import styles from "./atlas-switch.scss";

import "@/components/display/atlas-text/atlas-text";

export type SwitchProps = FormElementProps &
    WithTooltipProps & {
        "label": string;
        "description": string;
        "checked": boolean;
        "label-before": boolean;
    };

/**
 * @dependency atlas-text
 *
 * @event {CustomEvent} atlas-switch-change - Evento disparado quando o switch é marcado ou desmarcado
 *
 * @tag atlas-switch
 */
@customElement("atlas-switch")
export default class AtlasSwitch extends WithTooltipMixin(FormElement) {
    static styles = styles;

    /** O texto que aparece ao lado do switch */
    @property({ type: String }) label = "";

    /** A descrição do switch (posicionada abaixo da label) */
    @property({ type: String }) description = "";

    /** Indica que o switch está marcado */
    @property({ type: Boolean, reflect: true }) checked: boolean = false;

    /** Indica que a label deve vir antes do switch (posicionada ao lado esquerdo) */
    @property({ type: Boolean, attribute: "label-before" }) labelBefore = false;

    /**
     * Obtém o valor booleano correspondente ao estado de marcação do switch
     * @returns {boolean} - `true` se o switch estiver marcado, `false` caso contrário
     * @override
     */
    public getElementValue(): boolean {
        return this.checked;
    }

    /**
     * Define o foco no switch
     */
    public focus() {
        (this.shadowRoot.querySelector(".form-check-input") as HTMLInputElement).focus();
    }

    /**
     * Marca o switch
     */
    public check() {
        this.checked = true;
    }

    /**
     * Desmarca o switch
     */
    public uncheck() {
        this.checked = false;
    }

    /**
     * Habilita o switch
     */
    public enable() {
        this.disabled = false;
    }

    /**
     * Desabilita o switch
     */
    public disable() {
        this.disabled = true;
    }

    /**
     * Alterna o estado do switch
     * @param checked - Indica se o switch deve ser marcado ou desmarcado, caso não seja definido, o estado atual será invertido
     */
    public toggleChecked(checked?: boolean) {
        if (typeof checked === "boolean") {
            this.checked = checked;
        } else {
            this.checked = !this.checked;
        }
    }

    private handleChange() {
        if (!this.disabled) {
            this.checked = !this.checked;

            emit(this, "atlas-switch-change");
            emit(this, "atlas-form-element-touch");
        }
    }

    private renderLabel() {
        if (this.description) {
            return html`
                <label class="form-check-label with-description" for="${this.id || "checkbox_id"}">
                    <span class="label"> ${this.label} </span>
                    <div>
                        <atlas-text muted size="xs">${this.description}</atlas-text>
                    </div>
                </label>
            `;
        }
        return html`<label class="form-check-label" for="${this.id || "checkbox_id"}">${this.label}</label>`;
    }

    private renderSwitchHandle() {
        return html`
            <svg
                class="switch-handle"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                @click=${this.handleChange}
            >
                <circle cx="10" cy="10" r="10" fill="#000000" />
            </svg>
        `;
    }

    private renderSwitch() {
        const switchClass = {
            "form-check-input": true,
            [`is-${this._status}`]: this.getShowStatus()
        };

        return html`
            <span class="switch-input-container" data-atlas-tooltip="switch-tooltip">
                <input
                    class="${classMap(switchClass)}"
                    type="checkbox"
                    role="switch"
                    id="${this.id || "checkbox_id"}"
                    name="${this.name}"
                    .checked=${this.checked}
                    .value=${this.value}
                    ?required=${this.required}
                    ?disabled=${this.disabled}
                    aria-labelledby="${this.id}"
                    aria-checked="${this.checked}"
                    aria-disabled="${this.disabled}"
                    aria-required="${this.required}"
                    @change="${this.handleChange}"
                />
                ${this.renderSwitchHandle()}
            </span>
        `;
    }

    private renderSwitchSkeleton() {
        return html`
            <span class="switch-input-container">
                <input class="form-check-input skeleton"></input>
            </span>
        `;
    }

    /** @override */
    protected renderElement() {
        const switchContainerClass = {
            "form-check": true,
            "form-switch": true,
            "form-check-disabled": this.disabled,
            "has-description": !!this.description,
            "has-label": !!this.label
        };

        const content = when(
            this.labelBefore,
            () => html` ${this.renderLabel()} ${this.renderSwitch()} `,
            () => html` ${this.renderSwitch()} ${this.renderLabel()} `
        );

        return html`
            <div class="${classMap(switchContainerClass)}">${content}</div>
            ${this.renderTooltip("switch-tooltip")}
        `;
    }

    /** @override */
    protected renderSkeleton() {
        const switchSkeletonClass = {
            "form-check": true,
            "form-switch": true,
            "has-description": !!this.description,
            "has-label": !!this.label
        };

        return html`
            <div class="${classMap(switchSkeletonClass)}">
                ${when(!this.labelBefore, () => html` ${this.renderSwitchSkeleton()}`)}
                ${when(
                    this.description,
                    () => html`
                        <label class="form-check-label with-description with-skeleton-loading">
                            <atlas-text size="xs"></atlas-text>
                            <atlas-text size="xs"></atlas-text>
                        </label>
                    `,
                    () => html`<label class="form-check-label skeleton"></label>`
                )}
                ${when(this.labelBefore, () => html`${this.renderSwitchSkeleton()}`)}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-switch": AtlasSwitch;
    }
}
